import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import type RouterService from '@ember/routing/router-service';
import type { Store } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type FeaturesService from 'tio-common/services/features';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import {
  dynamicEligibilityRequest,
  periodsOfEligibilityRequest,
} from 'tio-data/requests/tas-participant-actions';
import { loadTasApplicationHistory } from 'tio-data/requests/tas-application-actions';
import type { DynamicEligibility, PeriodOfEligibility } from 'tio-common/types/tuition-assistance';
import { guidedWizardRouteFor } from 'tio-employee/utils/tas/guided-wizard-route';

interface TasApplicationShowRoute {
  application: TasApplicationModel;
  dynamicEligibility: boolean;
  programEligibility: string[];
}

export default class AuthenticatedTasApplicationsShowRoute extends Route {
  @service declare features: FeaturesService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare tuitionAssistance: TuitionAssistanceService;

  includes = [
    'tas-courses',
    'tas-courses.tas-assets.attachments',
    'tas-program-instance',
    // Program-level documents, surfaced as prior-step history on the attachments
    // section for type 2/3 (where the program instance owns the agreement /
    // approval docs). Relationships here are `async: false`, so an omitted
    // include resolves empty instead of lazy-loading — `allAttachments` was
    // silently contributing nothing from the program instance without this.
    'tas-program-instance.tas-assets.attachments',
    'tas-program-instance.tas-program-template',
    'tas-program-instance.tas-program-template.conditions.public-assets',
    'tas-program-instance.tas-program-template.tas-field-options',
    'tas-program-instance.tas-program-template.company.company-setting',
    'tas-program-instance.employee.person',
    'tas-program-instance.employee.tas-participant',
    'tas-assets.attachments',
    'tas-assistances',
    'tas-assistances.attachments',
    'tas-assistances.tas-assets',
    'tas-assistances.tas-assets.attachments',
    'tags',
    'comments',
    'pcs-cards.pcs-card-holder',
    'pcs-cards.pcs-company',
    'pcs-cards.pcs-authorizations',
    'pcs-cards.pcs-card-events',
    'pcs-cards.pcs-transactions',
  ];

  queryParams = {
    applicationSubmitted: { refreshModel: true },
  };

  async model({ tas_application_id }: { tas_application_id: string }) {
    const { content: applicationContent } = (await this.store.request(
      findRecord<TasApplicationModel>('tas-application', tas_application_id, {
        include: this.includes.join(','),
        reload: true,
      })
    )) as { content: { data: TasApplicationModel } };
    const application = applicationContent.data;

    await this.tuitionAssistance.loadEligibilitiesForCurrentEmployee();

    await loadTasApplicationHistory(this.store, application);

    const id = application.tasProgramInstance.employee.tasParticipant.id as string;
    const { code } = application.tasProgramInstance.tasProgramTemplate;

    const [dynamicResponse, periodsResponse] = await Promise.all([
      this.store.request(dynamicEligibilityRequest(id)),
      this.store.request(periodsOfEligibilityRequest(id)),
    ]);
    // Legacy adapter filtered the per-program-code value off the raw response
    // (`programCode ? all[code] : all`); parity keeps the bare lookup.
    const dynamicEligibility = (dynamicResponse.content as DynamicEligibility)[code] as boolean;
    const programEligibility = (periodsResponse.content as PeriodOfEligibility)[
      code
    ] as unknown as string[];

    return { application, dynamicEligibility, programEligibility };
  }

  afterModel(model: TasApplicationShowRoute) {
    // Table shared with the manage route (utils/tas/guided-wizard-route) so the two
    // detail screens cannot disagree about when a draft is bounced to the wizard.
    const route = guidedWizardRouteFor(
      model.application.typeClassification,
      model.application.state
    );
    if (route) {
      this.router.replaceWith(route, model.application.id);
    }
  }
}
